import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Header } from '@/components/layout/header/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Smartphone, CheckCircle2, Share, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function Install() {
  useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Detectar se já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Capturar evento de instalação
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Detectar quando app é instalado
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      toast({
        title: translateInterfaceText("App instalado com sucesso!"),
        description: translateInterfaceText("Você pode acessar o HostVSL pela tela inicial do seu dispositivo."),
      });
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [toast]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast({
        title: translateInterfaceText("Instalação não disponível"),
        description: translateInterfaceText("O app já está instalado ou seu navegador não suporta instalação."),
        variant: "destructive",
      });
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstallable(false);
      toast({
        title: translateInterfaceText("Instalando..."),
        description: translateInterfaceText("O HostVSL está sendo instalado no seu dispositivo."),
      });
    }

    setDeferredPrompt(null);
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  return (
    <Layout>
      <Header />
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#2563EB]/10 mb-6">
              <Smartphone className="w-10 h-10 text-[#2563EB]" />
            </div>
            <h1 className="text-4xl font-bold text-[#111] mb-4">
              {translateInterfaceText("Instale o HostVSL")}</h1>
            <p className="text-lg text-[#666] max-w-2xl mx-auto">
              {translateInterfaceText("Acesse suas métricas e vídeos diretamente da tela inicial do seu dispositivo. Funciona offline e carrega instantaneamente!")}</p>
          </div>

          {/* Status Card */}
          {isInstalled ? (
            <Card className="mb-8 border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-green-900">{translateInterfaceText("App já instalado!")}</h3>
                    <p className="text-sm text-green-700">
                      {translateInterfaceText("Você pode acessar o HostVSL pela tela inicial do seu dispositivo.")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : isInstallable ? (
            <Card className="mb-8 border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Button
                    onClick={handleInstallClick}
                    size="lg"
                    className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    {translateInterfaceText("Instalar Agora")}</Button>
                  <p className="text-sm text-blue-700 mt-3">
                    {translateInterfaceText("Clique no botão acima para instalar o app no seu dispositivo")}</p>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Instruções para iOS */}
          {isIOS && isSafari && !isInstalled && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share className="w-5 h-5 text-[#2563EB]" />
                  {translateInterfaceText("Instruções para iPhone/iPad")}</CardTitle>
                <CardDescription>
                  {translateInterfaceText("Siga estes passos para instalar no Safari")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2563EB]/10 flex items-center justify-center font-semibold text-[#2563EB]">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-[#111]">{translateInterfaceText("Toque no botão de compartilhar")}</p>
                    <p className="text-sm text-[#666]">
                      {translateInterfaceText("Ícone")}{" "}<Share className="w-4 h-4 inline" /> {" "}{translateInterfaceText("na barra inferior do Safari")}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2563EB]/10 flex items-center justify-center font-semibold text-[#2563EB]">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-[#111]">{translateInterfaceText("Role para baixo e toque em \"Adicionar à Tela de Início\"")}</p>
                    <p className="text-sm text-[#666]">
                      {translateInterfaceText("Procure pelo ícone")}{" "}<Plus className="w-4 h-4 inline" /> {" "}{translateInterfaceText("no menu")}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2563EB]/10 flex items-center justify-center font-semibold text-[#2563EB]">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-[#111]">{translateInterfaceText("Toque em \"Adicionar\"")}</p>
                    <p className="text-sm text-[#666]">
                      {translateInterfaceText("O ícone do HostVSL aparecerá na sua tela inicial")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Benefícios */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-[#2563EB]/10 flex items-center justify-center mb-4">
                  <Download className="w-6 h-6 text-[#2563EB]" />
                </div>
                <CardTitle className="text-lg">{translateInterfaceText("Acesso Rápido")}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#666]">
                  {translateInterfaceText("Abra o app direto da tela inicial, sem precisar abrir o navegador")}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-[#2563EB]/10 flex items-center justify-center mb-4">
                  <Smartphone className="w-6 h-6 text-[#2563EB]" />
                </div>
                <CardTitle className="text-lg">{translateInterfaceText("Experiência Nativa")}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#666]">
                  {translateInterfaceText("Interface otimizada que se comporta como um app nativo")}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-[#2563EB]/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-[#2563EB]" />
                </div>
                <CardTitle className="text-lg">{translateInterfaceText("Funciona Offline")}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#666]">
                  {translateInterfaceText("Acesse suas métricas mesmo sem conexão com a internet")}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
